// Тонкий wrapper над window.Telegram.WebApp. Скрипт telegram-web-app.js
// подключён в index.html — здесь только типизация и хелперы.

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  accent_text_color?: string;
  destructive_text_color?: string;
}

export interface TelegramWebApp {
  initData: string;
  colorScheme: "light" | "dark";
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  ready(): void;
  expand(): void;
  close(): void;
  HapticFeedback: {
    impactOccurred(style: "light" | "medium" | "heavy"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
  };
  MainButton: {
    text: string;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    setText(text: string): void;
    enable(): void;
    disable(): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

// В dev (vite serve без Telegram) у нас нет Telegram объекта. Делаем заглушку,
// чтобы можно было тестировать UI в обычном браузере.
const FALLBACK_INIT_DATA = (() => {
  if (typeof window === "undefined") return "";
  // Если в URL есть ?tgWebAppData=... — берём оттуда (грубая dev-имитация).
  const hash = window.location.hash;
  const m = hash.match(/tgWebAppData=([^&]+)/);
  return m && m[1] ? decodeURIComponent(m[1]) : "";
})();

export function getTelegram(): TelegramWebApp | null {
  return typeof window !== "undefined" ? window.Telegram?.WebApp ?? null : null;
}

export function getInitData(): string {
  const tg = getTelegram();
  return tg?.initData || FALLBACK_INIT_DATA;
}

// Применяет themeParams Telegram'а к CSS-переменным, которые читает Tailwind.
// Вызывается один раз при старте App.
export function applyTelegramTheme(): void {
  const tg = getTelegram();
  const root = document.documentElement;
  const t = tg?.themeParams ?? {};

  // Дефолты — на случай dev'а без Telegram (читаемая light-тема).
  const def = {
    bg: "#ffffff",
    bgSecondary: "#f3f4f6",
    text: "#111827",
    hint: "#6b7280",
    link: "#3b82f6",
    button: "#3b82f6",
    buttonText: "#ffffff",
    accent: "#3b82f6",
    destructive: "#ef4444",
  };

  root.style.setProperty("--tg-bg", t.bg_color ?? def.bg);
  root.style.setProperty("--tg-bg-secondary", t.secondary_bg_color ?? def.bgSecondary);
  root.style.setProperty("--tg-text", t.text_color ?? def.text);
  root.style.setProperty("--tg-hint", t.hint_color ?? def.hint);
  root.style.setProperty("--tg-link", t.link_color ?? def.link);
  root.style.setProperty("--tg-button", t.button_color ?? def.button);
  root.style.setProperty("--tg-button-text", t.button_text_color ?? def.buttonText);
  root.style.setProperty("--tg-accent", t.accent_text_color ?? def.accent);
  root.style.setProperty("--tg-destructive", t.destructive_text_color ?? def.destructive);

  if (tg?.colorScheme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}
